import { z } from 'zod';
import { DonationTypeSchema, DonationStatusSchema, DonorRefTypeSchema } from '../enums';

// Donor Reference Schema
export const DonorRefSchema = z.object({
  kind: DonorRefTypeSchema,
  id: z.string() // Reference to DonorID or GuestDonorID
});

export type DonorRef = z.infer<typeof DonorRefSchema>;

// Print Metadata Schema
export const PrintMetaSchema = z.object({
  printerName: z.string().optional(),
  attempts: z.number().int().min(0).default(0),
  lastError: z.string().optional(),
  lastAttemptTime: z.date().optional(),
  receiptNumber: z.string().optional()
});

export type PrintMeta = z.infer<typeof PrintMetaSchema>;

// Base Donation Schema
export const BaseDonationSchema = z.object({
  donationId: z.string().optional(), // MongoDB ObjectId
  donationType: DonationTypeSchema,
  donorRef: DonorRefSchema,
  amount: z.number().positive('Amount must be positive').max(10000000, 'Amount too large'),
  time: z.date().optional(), // Server time when recorded
  recordedBy: z.string(), // Reference to UserID
  deviceId: z.string().min(1, 'Device ID is required'),
  officeNo: z.number().int().positive('Office number must be positive'),
  regionId: z.number().int().positive('Region ID must be positive'),
  status: DonationStatusSchema.default('pending'),
  printAttempted: z.boolean().default(false),
  printMeta: PrintMetaSchema.optional(),
  remarks: z.string().max(500, 'Remarks too long').optional(),
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional()
});

export type BaseDonation = z.infer<typeof BaseDonationSchema>;

// Donation Creation Schema
export const CreateDonationSchema = BaseDonationSchema.omit({
  donationId: true,
  time: true,
  status: true,
  printAttempted: true,
  printMeta: true,
  createdAt: true,
  updatedAt: true
});

export type CreateDonation = z.infer<typeof CreateDonationSchema>;

// Donation Update Schema
export const UpdateDonationSchema = BaseDonationSchema.partial().extend({
  donationId: z.string() // Required for updates
});

export type UpdateDonation = z.infer<typeof UpdateDonationSchema>;

// Donation Response Schema (for API responses)
export const DonationResponseSchema = BaseDonationSchema.extend({
  donationId: z.string()
});

export type DonationResponse = z.infer<typeof DonationResponseSchema>;

// Donation with Relations Schema
export const DonationWithRelationsSchema = DonationResponseSchema.extend({
  donor: z.object({
    id: z.string(),
    name: z.string(),
    type: DonorRefTypeSchema
  }).optional(),
  office: z.object({
    officeNo: z.number(),
    officeName: z.string(),
    regionId: z.number(),
    regionName: z.string()
  }).optional(),
  recordedByUser: z.object({
    userId: z.string(),
    userName: z.string(),
    userRole: z.string()
  }).optional(),
  txnNumber: z.string().optional() // Sequential transaction number per office/day
});

export type DonationWithRelations = z.infer<typeof DonationWithRelationsSchema>;

// Donation List Query Schema
export const DonationListQuerySchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  officeNo: z.number().int().positive().optional(),
  regionId: z.number().int().positive().optional(),
  donorId: z.string().optional(),
  donationType: DonationTypeSchema.optional(),
  status: DonationStatusSchema.optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  minAmount: z.number().positive().optional(),
  maxAmount: z.number().positive().optional(),
  recordedBy: z.string().optional(),
  sortBy: z.enum(['time', 'amount', 'donationType', 'createdAt']).default('time'),
  sortOrder: z.enum(['asc', 'desc']).default('desc')
});

export type DonationListQuery = z.infer<typeof DonationListQuerySchema>;

// Donation Confirmation Schema
export const DonationConfirmationSchema = z.object({
  donationId: z.string(),
  receiptNumber: z.string().min(1, 'Receipt number is required'),
  printerName: z.string().optional(),
  printSuccess: z.boolean()
});

export type DonationConfirmation = z.infer<typeof DonationConfirmationSchema>;

// Donation Print Schema
export const DonationPrintSchema = z.object({
  donationId: z.string(),
  printerName: z.string().optional(),
  isReprint: z.boolean().default(false)
});

export type DonationPrint = z.infer<typeof DonationPrintSchema>;

// Donation Statistics Schema
export const DonationStatisticsSchema = z.object({
  totalDonations: z.number(),
  totalAmount: z.number(),
  byType: z.array(z.object({
    type: DonationTypeSchema,
    count: z.number(),
    amount: z.number()
  })),
  byStatus: z.array(z.object({
    status: DonationStatusSchema,
    count: z.number(),
    amount: z.number()
  })),
  byOffice: z.array(z.object({
    officeNo: z.number(),
    officeName: z.string(),
    count: z.number(),
    amount: z.number()
  })),
  period: z.object({
    start: z.date(),
    end: z.date()
  })
});

export type DonationStatistics = z.infer<typeof DonationStatisticsSchema>;

// Donation Receipt Schema
export const DonationReceiptSchema = z.object({
  donationId: z.string(),
  receiptNumber: z.string(),
  donorName: z.string(),
  donationType: DonationTypeSchema,
  amount: z.number(),
  date: z.date(),
  officeName: z.string(),
  recordedBy: z.string(),
  isReprint: z.boolean().default(false)
});

export type DonationReceipt = z.infer<typeof DonationReceiptSchema>; 